/**
 * threadArchiveNotice — announce a thread being archived FOR EVERYONE
 * (owner directive 2026-08-26).
 *
 * "If a user is currently within a thread that another user is actively
 * archiving, there should be a 5 second delay and a message clear as day that
 * appears (much like in the style of how workspaces are changed) … then the
 * other users are safely taken to the next best thread."
 *
 * Module-level store + subscribe, the workspaceTransition.ts pattern: the
 * thread store (useChatThreads) detects the remote archive and owns the
 * timing + navigation; the chrome (ThreadArchiveNoticeOverlay, mounted by
 * GlobalChatSystem) subscribes here and only ever displays. Neither imports
 * the other.
 *
 * Three kinds:
 *   • 'blocking' — the viewer is INSIDE the thread. Full overlay, the 5s
 *     hold, then the store navigates them to the next best thread and clears.
 *   • 'passing' — the thread was archived while the viewer was elsewhere.
 *     A transient toast-style line; no navigation, no hold.
 *   • 'refused' — the viewer tried to OPEN a thread that is closed to them
 *     (2026-09-21). The door is `switchThread`, which every navigating
 *     surface funnels through; this is how the refusal is announced instead
 *     of a press that silently does nothing. Same transient pill as
 *     'passing', no navigation, no hold — nothing moved, so there is nothing
 *     to move them off.
 */

/** The owner-directed hold before the viewer is moved off the thread. */
export const THREAD_ARCHIVE_NOTICE_HOLD_MS = 5_000;
/** How long a passing notice stays up (matches the archive toast rhythm). */
export const THREAD_ARCHIVE_PASSING_NOTICE_MS = 5_000;

export interface ThreadArchiveNotice {
  kind: 'blocking' | 'passing' | 'refused';
  threadId: string;
  threadTitle: string;
  /** Who archived it, when the fanout carried a name. Empty ⇒ generic copy. */
  archivedByName?: string;
}

type Listener = (notice: ThreadArchiveNotice | null) => void;

let current: ThreadArchiveNotice | null = null;
const listeners = new Set<Listener>();

export function getThreadArchiveNotice(): ThreadArchiveNotice | null {
  return current;
}

export function subscribeThreadArchiveNotice(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  // SNAPSHOT BEFORE THE LOOP, and iterate a COPY. Both halves are load-bearing
  // and neither was here (2026-09-21): `current` was read INSIDE the callback
  // while GlobalChatSystem's own subscriber calls clearThreadArchiveNotice
  // synchronously — which re-enters this function and nulls `current`
  // mid-iteration, so every subscriber after the first was handed `null` and
  // then nothing. No second consumer of this bus could ever have worked, which
  // is why nothing had noticed: there was only ever one. The third kind below
  // makes that two.
  const notice = current;
  for (const listener of Array.from(listeners)) {
    try {
      listener(notice);
    } catch { /* one bad listener must not mute the rest */ }
  }
}

export function beginThreadArchiveNotice(notice: ThreadArchiveNotice): void {
  // A blocking notice is never displaced by a quieter one — being moved off
  // the OPEN thread is the announcement that must not be missed, and that now
  // holds for the refusal too: a press that went nowhere matters less than
  // being taken out of the conversation you are reading.
  if (current?.kind === 'blocking' && notice.kind !== 'blocking') return;
  current = notice;
  emit();
}

export function clearThreadArchiveNotice(threadId?: string): void {
  if (threadId && current && current.threadId !== threadId) return;
  current = null;
  emit();
}
